import React from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TrendingUp, TrendingDown, Users, DollarSign, Shield } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface TimeRange {
  from: Date | null
  to: Date | null
  preset?: string
}

interface SourcePerformanceProps {
  timeRange: TimeRange
}

const sourceData = [
  {
    source: 'Everflow - Secco Squared (18)',
    type: 'Affiliate',
    totalLeads: 1234,
    fraudCount: 395,
    fraudRate: 32.0,
    costImpact: 11850,
    qualityScore: 68,
    trend: 'declining',
    revenue: 24680,
    conversionRate: 8.2,
    avgLeadValue: 20
  },
  {
    source: 'Direct Traffic',
    type: 'Organic',
    totalLeads: 892,
    fraudCount: 18,
    fraudRate: 2.0,
    costImpact: 540,
    qualityScore: 98,
    trend: 'stable',
    revenue: 17840,
    conversionRate: 15.3,
    avgLeadValue: 20
  },
  {
    source: 'Email Partner A',
    type: 'Email',
    totalLeads: 567,
    fraudCount: 159,
    fraudRate: 28.1,
    costImpact: 4770,
    qualityScore: 72,
    trend: 'improving',
    revenue: 8164,
    conversionRate: 9.8,
    avgLeadValue: 20
  },
  {
    source: 'Google Ads',
    type: 'Paid',
    totalLeads: 423,
    fraudCount: 21,
    fraudRate: 5.0,
    costImpact: 630,
    qualityScore: 95,
    trend: 'stable',
    revenue: 8460,
    conversionRate: 12.7,
    avgLeadValue: 20
  },
  {
    source: 'Facebook Ads',
    type: 'Paid',
    totalLeads: 345,
    fraudCount: 48,
    fraudRate: 13.9,
    costImpact: 1440,
    qualityScore: 86,
    trend: 'improving',
    revenue: 5865,
    conversionRate: 11.2,
    avgLeadValue: 20
  },
  {
    source: 'Partner Network B',
    type: 'Affiliate',
    totalLeads: 234,
    fraudCount: 75,
    fraudRate: 32.1,
    costImpact: 2250,
    qualityScore: 68,
    trend: 'declining',
    revenue: 3276,
    conversionRate: 7.9,
    avgLeadValue: 20
  }
]

const monthlyTrends = [
  { month: 'Jan', everflow: 35, email: 31, google: 6, facebook: 15, direct: 2 },
  { month: 'Feb', everflow: 33, email: 29, google: 5, facebook: 14, direct: 2 },
  { month: 'Mar', everflow: 34, email: 30, google: 4, facebook: 13, direct: 2 },
  { month: 'Apr', everflow: 32, email: 28, google: 5, facebook: 12, direct: 2 },
  { month: 'May', everflow: 31, email: 27, google: 5, facebook: 14, direct: 2 },
  { month: 'Jun', everflow: 32, email: 28, google: 5, facebook: 14, direct: 2 }
]

const getTrendIcon = (trend: string) => {
  switch (trend) {
    case 'improving':
      return <TrendingUp className="h-4 w-4 text-green-500" />
    case 'declining':
      return <TrendingDown className="h-4 w-4 text-red-500" />
    default:
      return <div className="h-4 w-4 bg-gray-400 rounded-full" />
  }
}

const getQualityColor = (score: number) => {
  if (score >= 90) return 'text-green-600'
  if (score >= 75) return 'text-yellow-600'
  return 'text-red-600'
}

const getFraudRateColor = (rate: number) => {
  if (rate <= 5) return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
  if (rate <= 15) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
  return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border rounded-lg shadow-lg p-3">
        <p className="font-semibold text-foreground">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.dataKey}: {entry.value}%
          </p>
        ))}
      </div>
    )
  }
  return null
}

export const SourcePerformance: React.FC<SourcePerformanceProps> = ({ timeRange }) => {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">Total Sources</p>
                <p className="text-2xl font-bold">8</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-red-500" />
              <div>
                <p className="text-sm text-muted-foreground">High-Risk Sources</p>
                <p className="text-2xl font-bold">2</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold">$68K</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingDown className="h-8 w-8 text-orange-500" />
              <div>
                <p className="text-sm text-muted-foreground">Avg Fraud Rate</p>
                <p className="text-2xl font-bold">18.5%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fraud Rate Trends */}
      <Card>
        <CardHeader>
          <CardTitle>Fraud Rate Trends by Source</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis label={{ value: 'Fraud Rate (%)', angle: -90, position: 'insideLeft' }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="everflow" stroke="#EF4444" strokeWidth={2} name="Everflow" />
                <Line type="monotone" dataKey="email" stroke="#F97316" strokeWidth={2} name="Email Partners" />
                <Line type="monotone" dataKey="google" stroke="#10B981" strokeWidth={2} name="Google Ads" />
                <Line type="monotone" dataKey="facebook" stroke="#3B82F6" strokeWidth={2} name="Facebook" />
                <Line type="monotone" dataKey="direct" stroke="#8B5CF6" strokeWidth={2} name="Direct" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Source Performance Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Source Performance Analysis</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                Export Data
              </Button>
              <Button variant="outline" size="sm">
                Configure Thresholds
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Source</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Total Leads</TableHead>
                <TableHead className="text-right">Fraud Rate</TableHead>
                <TableHead className="text-right">Cost Impact</TableHead>
                <TableHead className="text-right">Quality Score</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-center">Trend</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sourceData.map((source, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{source.source}</p>
                      <p className="text-xs text-muted-foreground">
                        Conv: {source.conversionRate}% • AVG: ${source.avgLeadValue}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{source.type}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {source.totalLeads.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge className={getFraudRateColor(source.fraudRate)}>
                      {source.fraudRate}%
                    </Badge>
                    <div className="text-xs text-muted-foreground mt-1">
                      {source.fraudCount} fraudulent
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-semibold text-red-600">
                      ${source.costImpact.toLocaleString()}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={`font-semibold ${getQualityColor(source.qualityScore)}`}>
                      {source.qualityScore}/100
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-semibold text-green-600">
                      ${source.revenue.toLocaleString()}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      {getTrendIcon(source.trend)}
                      <span className="text-sm capitalize">{source.trend}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Performance vs Cost Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Quality Score vs Revenue Impact</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sourceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="source" angle={-45} textAnchor="end" height={100} />
                <YAxis yAxisId="left" orientation="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Bar yAxisId="left" dataKey="qualityScore" fill="#10B981" name="Quality Score" />
                <Bar yAxisId="right" dataKey="revenue" fill="#3B82F6" name="Revenue ($)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}